import { fanduel as fd } from "@fanduel/formation-tokens";
import cx from "classnames";
import * as d3 from "d3";
import { useEffect } from "react";
import { TeamDisplay } from "./LineMovement";
import { marginLeft, marginRight, stroke } from "./LinePlot";
import { formatAmericanOddsLabels, formatOddString } from "./utils";
import styles from "./lineChart.module.scss";

interface ChartAxisProps {
  svgId: string;
  market: string;
  gameId: number;
  y: d3.ScaleLinear<number, number, never>;
  oddsSuggestedSegments: number;
  timeScale: d3.ScaleTime<number, number, never>;
  startDate: Date;
  endDate: Date;
  width: number;
  show: TeamDisplay;
  toggle: boolean;
  showUnifiedLine: boolean;
}

const ChartAxis = ({
  svgId,
  market,
  gameId,
  y,
  oddsSuggestedSegments,
  timeScale,
  startDate,
  endDate,
  width,
  show,
  toggle,
  showUnifiedLine,
}: ChartAxisProps) => {
  useEffect(() => {
    const svgElement = d3.select(`#${svgId}`);
    const axisRightID = `axis-right-${market}-${gameId}`;
    const axisBottomID = `axis-bottom-${market}-${gameId}`;
    const horizontalLinesClass = `horizontal-line-${market}-${gameId}`;

    // Clear old before adding new to prevent stacking.
    svgElement.selectAll(`#${axisRightID}`).remove();
    svgElement.selectAll(`#${axisBottomID}`).remove();
    svgElement.selectAll(`.${horizontalLinesClass}`).remove();

    const tickValues = y.ticks(oddsSuggestedSegments);

    const formatTicks = (tick: d3.NumberValue): string => {
      if (showUnifiedLine) {
        return tick.toString();
      }
      if (toggle && market !== "Spread") {
        return tick.toString();
      }
      if (toggle) {
        return formatOddString(tick);
      }
      return formatAmericanOddsLabels(tick);
    };

    const yAxisGen = d3
      .axisRight(y)
      .tickValues(tickValues)
      .tickSize(0)
      .tickFormat(formatTicks);

    svgElement
      .append("g")
      .attr("class", cx(styles.hideLine, styles.axisRight))
      .attr("id", axisRightID)
      .call(yAxisGen);

    let dayInterval = 1;
    const totalDays = d3.timeDay.count(startDate, endDate);
    if (totalDays > oddsSuggestedSegments) {
      dayInterval = Math.round((totalDays / oddsSuggestedSegments) * 1.2);
    }

    const findRegularDayIntervals = (d: Date) =>
      d3.timeDay.count(d, endDate) % dayInterval === 0;

    /* A custom function for generating which days show up on the axis is required here
    because the default function from D3 always returns the first and last day of a 
    month leading to irregular spacing */
    const timeAxisGen = d3
      .axisBottom(timeScale)
      .ticks(d3.timeDay.filter(findRegularDayIntervals))
      .tickSize(0)
      .tickFormat((date) => {
        return d3.timeFormat("%m/%d")(
          date instanceof Date ? date : new Date(Number(date))
        );
      });

    svgElement
      .append("g")
      .attr("class", cx(styles.hideLine, styles.axisBottom))
      .attr("id", axisBottomID)
      .call(timeAxisGen);

    tickValues.forEach((tickValue) => {
      svgElement
        .insert("line", ":first-child")
        .attr("x1", marginLeft)
        .attr("x2", width - marginRight)
        .attr("y1", y(tickValue))
        .attr("y2", y(tickValue))
        .attr("stroke", fd.colors.borderDefault)
        .attr("stroke-width", stroke / 10)
        .attr("class", horizontalLinesClass);
    });

    if (tickValues.includes(-100)) {
      svgElement
        .insert("line", ":first-child")
        .attr("x1", marginLeft)
        .attr("x2", width - marginRight)
        .attr("y1", y(-100))
        .attr("y2", y(-100))
        .attr("stroke", fd.colors.contentSubtle)
        .attr("stroke-width", stroke / 5)
        .attr("class", horizontalLinesClass);
    }
  }, [
    svgId,
    market,
    gameId,
    y,
    oddsSuggestedSegments,
    timeScale,
    width,
    show,
    startDate,
    endDate,
    toggle,
    showUnifiedLine,
  ]);
  return null;
};

export default ChartAxis;
